import { Column } from 'app/model/dataset/column';
import { NamedArray } from 'app/model/base/namedarray';
import { Model } from 'app/model/backendmodel';

import { Dataset } from 'app/model/dataset/dataset';
import { FormatDatasetV2 } from 'app/model/dataset/dataset.v2';
import { DatasetAdapter } from 'app/model/adapters/datasets/dataset.adapter';
import { IStructure } from './dataset.constant.adapter';
import {
    Metamodel,
    IModel,
    AdapterRegistry,
    ReferenceAdapter,
    ModelConstants,
    isLogicAppExpression,
    SharedModel
} from 'shell-models-lib';
import { Location } from 'app/model/dataset/location/location';
import { GenericLocationAdapter } from '../activities/copysource/generic.location.adapter';
import { FilePath } from 'app/model/dataset/file-path';
import { ValueAdapter } from '../value.adapter';
import _ from 'lodash';
import {
    ActivityConnectionContext,
    ActivityConnectionLocationContext
} from 'app/interfaces/copilot.pipeline.action.types';

export abstract class FormatDatasetAdapter<
    T extends FormatDatasetV2,
    U extends Model.DatasetV2Resource,
    D extends ActivityConnectionContext = ActivityConnectionContext
> extends DatasetAdapter<T, U, D> {
    public override async toFabricCopilotContext(dataset: T): Promise<D> {
        let dsContext = await super.toFabricCopilotContext(dataset);
        if (dataset.location) {
            const locationAdapter = this.context.adapterFor<
                Location,
                Model.DatasetLocation,
                ActivityConnectionLocationContext
            >(Metamodel.modelOf(dataset.location));
            if (locationAdapter != null) {
                const locationContext = await locationAdapter.toFabricCopilotContext(dataset.location);
                if (locationContext) {
                    dsContext = { ...dsContext, ...locationContext };
                }
            }
        }
        return dsContext;
    }

    public override async fromFabricCopilotContext(context: D, model?: T): Promise<T> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (model.location) {
                const locationAdapter = this.context.adapterFor<
                    Location,
                    Model.DatasetLocation,
                    ActivityConnectionLocationContext
                >(Metamodel.modelOf(model.location));
                if (locationAdapter != null) {
                    await locationAdapter.fromFabricCopilotContext(context, model.location);
                }
            }
        }
        return model;
    }

    protected async _toResource(dataset: T): Promise<U> {
        const datasetResource = (await super._toResource(dataset)) as U;

        if (__IS_FABRIC_DPE__ && dataset.linkedService) {
            const linkedServiceType = Metamodel.modelOf(dataset.linkedService).name;
            const linkedService = this.getArtifactLinkedService(datasetResource);
            if (linkedServiceType === ModelConstants.lakehouseLinkedService) {
                linkedService.properties.typeProperties.rootFolder = dataset.rootFolder;
            }
        }
        return datasetResource;
    }

    protected async _toModel(modelDatasetResource: U, dataset: T): Promise<T> {
        if (!dataset) {
            throw new Error('Dataset should be initialized before calling Dataset::_toModel');
        }

        if (__IS_FABRIC_DPE__) {
            const linkedService = this.getArtifactLinkedService(modelDatasetResource);
            const linkedServiceType = linkedService?.properties?.type;
            if (linkedServiceType === ModelConstants.lakehouseLinkedService) {
                dataset.rootFolder = linkedService.properties.typeProperties?.rootFolder;
            }
        }

        await super._toModel(modelDatasetResource, dataset);

        return dataset;
    }

    // TODO: change
    protected columnValueToResource(dataset: Dataset, columns: NamedArray<Column>): IStructure[] | undefined {
        return super.columnValueToResource(dataset, columns);
    }

    // TODO: change
    protected columnResourceToValue(dataset: Dataset, columns: IStructure[] | undefined): NamedArray<Column> {
        return super.columnResourceToValue(dataset, columns);
    }

    protected locationToResource(dataset?: T): Model.DatasetLocation {
        if (dataset && dataset.location) {
            const adapter = this.context.adapterFor(Metamodel.modelOf(dataset.location));
            if (adapter != null) {
                const result = adapter.toResource(dataset.location);

                // handle raw data
                if (dataset.location.raw != null) {
                    const parsed = JSON.parse(dataset.location.raw);
                    _.merge(result, parsed);
                }
                return result;
            }
        }
        return undefined;
    }

    protected async locationToModel(modelDatasetResource: Model.DatasetV2Resource, dataset?: T): Promise<void> {
        if (
            modelDatasetResource &&
            modelDatasetResource.properties &&
            modelDatasetResource.properties.typeProperties &&
            modelDatasetResource.properties.typeProperties.location &&
            modelDatasetResource.properties.typeProperties.location.type
        ) {
            let locationModel: IModel | undefined;
            try {
                locationModel = Metamodel.modelByName(modelDatasetResource.properties.typeProperties.location.type);
            } catch {
                // TODO emit telemetry.
            }
            if (!locationModel || !locationModel.name.endsWith('Location')) {
                /**
                 * for connector extensibility usage, when we hit unrecognized location resource type,
                 * we'd be able to load it back on UI to enable user edit it via JSON editor.
                 */
                locationModel = this._registerLocation(modelDatasetResource.properties.typeProperties.location.type);
            }
            const locationAdapter = this.context.adapterFor(locationModel);

            if (locationAdapter != null) {
                dataset.location = locationAdapter.toModel(
                    modelDatasetResource.properties.typeProperties.location,
                    dataset
                );

                // handle raw data
                if (dataset.location != null) {
                    dataset.location.raw = JSON.stringify(
                        await ValueAdapter.remainder(
                            this.context,
                            modelDatasetResource.properties.typeProperties.location,
                            locationModel
                        )
                    );
                }
            }

            if (
                this.context.adapterConfig.tolerateInvalidReference &&
                locationModel &&
                modelDatasetResource?.properties?.linkedServiceName &&
                !isLogicAppExpression(
                    (modelDatasetResource.properties.linkedServiceName as SharedModel.LinkedServiceReference)
                        .referenceName
                )
            ) {
                const linkedServiceModel = locationModel.tag('linkedServiceModel') as IModel;
                dataset.linkedService = ReferenceAdapter.toProxyModel(
                    linkedServiceModel,
                    modelDatasetResource.properties.linkedServiceName,
                    this.context
                );
            }
        }
    }

    private _registerLocation(locationType: string): IModel {
        const model = Metamodel.createModel(Location.MODEL, {
            name: locationType,
            display: [locationType],
            createdFromUnknownType: true
        });
        model.construct.prototype.setLocation = function setLocation(filePath: FilePath): void {
            return;
        };
        model.construct.prototype.getLocation = function getLocation(): FilePath {
            return new FilePath();
        };
        model.construct.prototype.getFolderPath = function getFolderPath(): string {
            return '';
        };
        model.construct.prototype.isFolder = function isFolder(): boolean {
            return false;
        };
        AdapterRegistry.createAdapter(
            {
                model,
                adapts: {
                    type: '*'
                }
            },
            GenericLocationAdapter
        );
        return model;
    }
}
